<template>
    <div class="audio-recorder-wrapper" :class="mode">
        
        <div class="col-auto">
            <button @click="handleCancel" type="button" class="btn icon-btn-delete" title="Cancelar e Excluir">
                <i class="ri-delete-bin-line"></i>
            </button>
        </div>

        <div class="col recorder-center">
            
            <div v-show="mode === 'recording' && !isEncoding" class="recording-display animate__animated animate__fadeIn">
                <div class="recording-indicator">
                    <div class="red-dot"></div>
                    <span class="status-text">Gravando...</span>
                </div>
                <span class="timer">{{ formatTime(currentDuration) }}</span>

                <div class="visualizer-container">
                    <canvas ref="canvasRef"></canvas>
                </div>
            </div>

            <div v-show="isEncoding" class="encoding-display">
                <span class="status-text">Convertendo áudio… {{ encodeProgress }}%</span>
                <button type="button" class="btn-action btn-cancel-encode" title="Cancelar conversão" @click="cancelEncoding">
                    <i class="ri-close-line"></i>
                </button>
            </div>

            <div v-show="mode === 'preview'" class="preview-player animate__animated animate__fadeIn">
                <button class="btn-preview-play" @click="togglePreviewPlay">
                    <i :class="isPreviewPlaying ? 'ri-pause-fill' : 'ri-play-fill'"></i>
                </button>
                
                <div class="seek-wrapper">
                    <input 
                        type="range" 
                        class="seek-slider" 
                        min="0" 
                        :max="previewDuration || 1" 
                        step="0.1"
                        v-model="previewCurrentTime"
                        @input="handleSeekInput"
                        :style="seekBackgroundStyle"
                    >
                </div>

                <span class="timer preview-timer">
                    {{ formatTime(previewCurrentTime) }} / {{ formatTime(previewDuration) }}
                </span>
            </div>

        </div>
        
        <div class="col-auto actions-right">
            <button
                v-if="mode === 'recording' && !isPcm"
                @click="pauseRecording"
                class="btn-action btn-pause"
                title="Pausar para ouvir"
            >
                <i class="ri-pause-line"></i>
            </button>

            <button 
                v-else 
                @click="resumeRecording" 
                class="btn-action btn-resume" 
                title="Continuar Gravando"
            >
                <i class="ri-mic-fill"></i>
            </button>
        </div>

        <audio v-liberar-midia ref="previewAudioRef" class="d-none" @timeupdate="onTimeUpdate" @ended="onPreviewEnded"></audio>
    </div>
</template>

<script setup lang="ts">
import { throttle } from '@/utils/throttle';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { branding } from '@/config/branding';
import { resolveAudioPath, nativeMimeForPath, pathEhOpus, type AudioPath, type FormatoDeAudio } from '@/utils/audio/audioPath';
import { PcmRecorder } from '@/utils/audio/pcmCapture';
import { encodePcmToM4a } from '@/utils/audio/webcodecsM4a';
import { transcodeToM4aViaWorker, warmUpM4aWorker, disposeM4aWorker } from '@/utils/audio/ffmpegM4a';
import { codecDeAudioMp4, SAMPLE_ENTRY_AAC } from '@/utils/audio/mp4Codec';
import { ehContainerWebm, remuxWebmOpusParaOgg } from '@/utils/audio/webmOpusOgg';

const emit = defineEmits<{ cancel: []; unsupported: [motivo?: string] }>();

// formato: o que o canal pede do gravador (ver `formatoDeAudioDoCanal`).
//   'opus' = WhatsApp Cloud — Ogg/Opus é o que vira NOTA DE VOZ;
//   'm4a'  = Instagram / Messenger / IG Login — só audio/mp4 toca lá;
//   'livre'= demais canais (comportamento legado).
// A estratégia é resolvida em runtime (audioPath.ts): Opus nativo, Path A
// (MediaRecorder mp4), Path B (WebCodecs) ou fallback WASM. Sem nenhum caminho
// viável, avisa o pai via 'unsupported'.
const props = withDefaults(defineProps<{ formato?: FormatoDeAudio }>(), { formato: 'livre' });


//  ESTADOS 
// Padronizado para 'mode'
const mode = ref<'recording' | 'preview'>('recording');
const mediaRecorder = ref<MediaRecorder | null>(null);
const audioChunks = ref<Blob[]>([]);
const currentDuration = ref(0);
let recordingTimerInterval: any = null;

const recordingMimeType = ref('audio/webm');

// Path B (captura PCM + encode AAC/MP4). Resolvido no mount.
const audioPath = ref<AudioPath>('native-webm');
// Só o caminho 'webcodecs' captura PCM. O 'wasm' grava nativo (MediaRecorder) e
// transcoda para audio/mp4 via ffmpeg.wasm ao enviar.
const isPcm = computed(() => audioPath.value === 'webcodecs');
// Só a cadeia m4a transcoda. No caminho Opus o blob já sai no formato final —
// e transcodar para AAC ali destruiria justamente a nota de voz.
const exigeM4a = computed(() => props.formato === 'm4a' || (props.formato === 'opus' && !pathEhOpus(audioPath.value)));
let pcmRecorder: PcmRecorder | null = null;
let sourceNode: MediaStreamAudioSourceNode | null = null;
const isEncoding = ref(false);
const encodeProgress = ref(0);
let encodeAbort: AbortController | null = null;

// VISUALIZADOR (Canvas) 
const canvasRef = ref<HTMLCanvasElement | null>(null);
const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const dataArray = ref<Uint8Array | null>(null);
let animationFrameId: number | null = null;
let streamRef: MediaStream | null = null;
const amplitudeHistory = ref<number[]>([]);
let samplingInterval: any = null;

// PREVIEW (Player) 
const previewAudioRef = ref<HTMLAudioElement | null>(null);
const isPreviewPlaying = ref(false);
const previewCurrentTime = ref(0);
const previewDuration = ref(0);
const previewBlobUrl = ref<string | null>(null);


let canvasContext: CanvasRenderingContext2D | null = null;
let displayWidth = 0; 
let displayHeight = 30; 


onMounted(async () => {
    if (!canvasRef.value) return;
    const canvas = canvasRef.value;
    
    const dpr = window.devicePixelRatio || 1;
    displayWidth = canvas.clientWidth; 
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr; 
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    
    canvasContext = canvas.getContext('2d');
    if (!canvasContext) return;
    canvasContext.scale(dpr, dpr);
    
    try {
        if (!navigator.mediaDevices?.getUserMedia) {
            emit('unsupported', 'Este navegador não expõe o microfone (getUserMedia). Use uma janela HTTPS no Chrome, Edge, Firefox ou Safari.');
            return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef = stream; 
        
        audioContext.value = new (window.AudioContext || (window as any).webkitAudioContext)();
        analyser.value = audioContext.value.createAnalyser();
        const source = audioContext.value.createMediaStreamSource(stream);
        source.connect(analyser.value);
        sourceNode = source;

        analyser.value.fftSize = 1024;
        dataArray.value = new Uint8Array(analyser.value.fftSize);

        // Resolve a estratégia (native-mp4 / native-webm / webcodecs / wasm / ogg-evolution).
        audioPath.value = await resolveAudioPath(props.formato, audioContext.value.sampleRate);

        // Caminho WASM: o core do ffmpeg tem alguns MB. Começa a baixar agora,
        // enquanto o usuário grava, para não haver espera ao soltar o botão.
        if (audioPath.value === 'wasm') warmUpM4aWorker();

        if (isPcm.value) {
            // Path B (webcodecs): captura PCM via AudioWorklet; o encode AAC/MP4
            // (AudioEncoder nativo) acontece ao enviar.
            pcmRecorder = new PcmRecorder(audioContext.value, source);
            await pcmRecorder.start();
        } else {
            recordingMimeType.value = nativeMimeForPath(audioPath.value);
            mediaRecorder.value = new MediaRecorder(stream, { mimeType: recordingMimeType.value });
            mediaRecorder.value.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunks.value.push(event.data);
            };
            mediaRecorder.value.start(100);
        }

        startTimer();

        samplingInterval = setInterval(sampleAmplitude, 70);

        drawWaveform();
        
    } catch (err) {
        console.error("Erro ao acessar o microfone:", err);
        emit('unsupported', mensagemDeErroDeMicrofone(err));
    }
});

/**
 * Mensagem em PT-BR para as falhas de `getUserMedia`. Antes o erro só ia para o
 * console e a barra de gravação fechava sozinha: quem via
 * `NotFoundError: Requested device not found` (perfil de navegador sem
 * dispositivo de entrada visível) não tinha como saber o que houve, e ainda
 * parecia que a permissão nem tinha sido pedida — ela não é pedida mesmo quando
 * não existe dispositivo algum para pedir.
 */
const mensagemDeErroDeMicrofone = (err: unknown): string => {
    const nome = (err as { name?: string } | null)?.name ?? '';
    switch (nome) {
        case 'NotFoundError':
        case 'OverconstrainedError':
            return 'Nenhum microfone encontrado. Conecte um dispositivo de entrada e verifique se o navegador enxerga o áudio do sistema.';
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Permissão de microfone negada. Libere o acesso ao microfone para este site nas configurações do navegador.';
        case 'NotReadableError':
        case 'AbortError':
            return 'O microfone está em uso por outro aplicativo ou não pôde ser aberto. Feche quem estiver usando e tente de novo.';
        default:
            return 'Não foi possível acessar o microfone.';
    }
};

// Lógica de Amostragem e Desenho
const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
    const r = Math.min(radius, width / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
};

const sampleAmplitude = () => {
    if (!analyser.value || !dataArray.value || mode.value !== 'recording') {
        return;
    }
    // @ts-expect-error não sei
    analyser.value.getByteTimeDomainData(dataArray.value); 
    
    let peak = 0;
    for (let i = 0; i < dataArray.value.length; i++) {
        const normalized = Math.abs(dataArray.value[i] - 128);
        if (normalized > peak) {
            peak = normalized;
        }
    }
    
    let val = peak / 128.0;
    
    val = val * 2.5; 
    
    if (val > 1) val = 1;

    amplitudeHistory.value.push(val);
};

const drawWaveform = () => {
    if (!canvasContext) return; 

    // Se não estiver gravando, para a animação
    if (mode.value !== 'recording') return;

    canvasContext.clearRect(0, 0, displayWidth, displayHeight);
    canvasContext.fillStyle = branding.colors.light.success;

    const barWidth = 2;
    const barGap = 1.5;
    const totalBarWidth = barWidth + barGap;
    
    const numBars = Math.floor(displayWidth / totalBarWidth);
    
    const history = amplitudeHistory.value;
    const startIndex = Math.max(0, history.length - numBars);

    for (let i = 0; i < numBars; i++) {
        const historyIndex = startIndex + i;
        if (historyIndex >= history.length) break; 
        
        const normalizedAmp = history[historyIndex] || 0;
        const boostedAmp = Math.sqrt(normalizedAmp);
        
        const minHeight = 2.0;
        const maxHeight = displayHeight * 0.95; 
        
        let barHeight = (boostedAmp * maxHeight) + minHeight; 
        if (barHeight > displayHeight) barHeight = displayHeight;

        const y = (displayHeight - barHeight) / 2;
        
        drawRoundedRect(canvasContext, i * totalBarWidth, y, barWidth, barHeight, 1);
    }
    
    animationFrameId = requestAnimationFrame(drawWaveform);
};

const startTimer = () => {
    clearInterval(recordingTimerInterval);
    recordingTimerInterval = setInterval(() => {
        currentDuration.value += 0.1;
    }, 100);
};

const stopTimer = () => {
    clearInterval(recordingTimerInterval);
};

const pauseRecording = () => {
    if (mediaRecorder.value && mediaRecorder.value.state === 'recording') {
        mediaRecorder.value.pause();
        mediaRecorder.value.requestData(); 
        stopTimer();
        
        // Pequeno delay para garantir estado
        setTimeout(() => {
            generatePreview();
            mode.value = 'preview'; 
        }, 100);
    }
};

const resumeRecording = () => {
    if (mediaRecorder.value && mediaRecorder.value.state === 'paused') {
        mediaRecorder.value.resume();
        mode.value = 'recording';
        startTimer();
        drawWaveform(); // Reinicia animação
    }
};

// Gera o blob para o preview
const generatePreview = () => {
    const blob = new Blob(audioChunks.value, { type: recordingMimeType.value });
    if (previewBlobUrl.value) URL.revokeObjectURL(previewBlobUrl.value);
    
    previewBlobUrl.value = URL.createObjectURL(blob);
    
    if (previewAudioRef.value) {
        previewAudioRef.value.src = previewBlobUrl.value;
        previewAudioRef.value.load();
        previewDuration.value = currentDuration.value;
        previewCurrentTime.value = 0;
    }
};


const togglePreviewPlay = () => {
    if (!previewAudioRef.value) return;
    if (isPreviewPlaying.value) {
        previewAudioRef.value.pause();
    } else {
        previewAudioRef.value.play();
    }
    isPreviewPlaying.value = !isPreviewPlaying.value;
};

const onTimeUpdate = () => {
    if (previewAudioRef.value) {
        previewCurrentTime.value = previewAudioRef.value.currentTime;
    }
};

const onPreviewEnded = () => {
    isPreviewPlaying.value = false;
    previewCurrentTime.value = 0;
};

const handleSeekInput = () => {
    if (previewAudioRef.value) {
        // Pausa para evitar glitches
        previewAudioRef.value.pause();
        isPreviewPlaying.value = false;
        
        const val = Number(previewCurrentTime.value);
        if (isFinite(val)) {
            previewAudioRef.value.currentTime = val;
        }
    }
};

const handleSeekChange = () => {
    // seila
};

const seekBackgroundStyle = computed(() => {
    const percent = (previewCurrentTime.value / previewDuration.value) * 100 || 0;
    return {
        background: `linear-gradient(to right, ${branding.colors.light.success} ${percent}%, #e9ecef ${percent}%)`
    };
});


const handleCancel = () => {
    if (encodeAbort) encodeAbort.abort();
    if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
        mediaRecorder.value.stop();
    }
    cleanup();
    emit('cancel');
};

const buildWaveform = (): number[] => {
    const samples = 40;
    const fullHistory = amplitudeHistory.value;
    let finalWaveform: number[] = [];

    if (fullHistory.length > 0) {
        const blockSize = Math.floor(fullHistory.length / samples);

        if (blockSize < 1) {
            finalWaveform = fullHistory.slice(0, samples);
            while (finalWaveform.length < samples) finalWaveform.push(0);
        } else {
            for (let i = 0; i < samples; i++) {
                const start = i * blockSize;
                let sum = 0;
                for (let j = 0; j < blockSize; j++) {
                    sum += fullHistory[start + j] || 0;
                }
                finalWaveform.push(sum / blockSize);
            }
        }

        const maxVal = Math.max(...finalWaveform);
        if (maxVal > 0) {
            const safeFactor = Math.min(1 / maxVal, 5);
            finalWaveform = finalWaveform.map(v => Math.max(0.15, Math.min(1, v * safeFactor)));
        } else {
            finalWaveform = Array(samples).fill(0.1);
        }
    } else {
        finalWaveform = Array(samples).fill(0.1);
    }

    return finalWaveform;
};

/** Para o MediaRecorder e monta o Blob nativo a partir dos chunks. */
const stopMediaRecorderBlob = (): Promise<Blob> => new Promise((resolve) => {
    if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
        mediaRecorder.value.stop();
    }
    // Aguarda o último `ondataavailable` (start(100)) antes de montar o blob.
    setTimeout(() => {
        resolve(new Blob(audioChunks.value, { type: recordingMimeType.value }));
    }, 200);
});

const capturarBlobBruto = async () => {
    const durationStr = formatTime(Math.floor(currentDuration.value));
    const waveform = buildWaveform();

    // Path B (webcodecs): para a captura PCM e encoda para audio/mp4 com o
    // AudioEncoder nativo (pode ser cancelado).
    if (isPcm.value) {
        const rec = pcmRecorder;
        const { samples, sampleRate } = rec
            ? rec.stop()
            : { samples: new Float32Array(0), sampleRate: 48000 };

        isEncoding.value = true;
        encodeProgress.value = 0;
        encodeAbort = new AbortController();
        try {
            const blob = await encodePcmToM4a(samples, sampleRate, {
                signal: encodeAbort.signal,
                onProgress: (f) => { encodeProgress.value = Math.round(f * 100); },
            });
            return { blob, duration: durationStr, waveform };
        } finally {
            isEncoding.value = false;
            encodeAbort = null;
            cleanup();
        }
    }

    // Fallback WASM ('wasm'): grava nativo (Firefox: webm/opus) e transcoda para
    // audio/mp4 via ffmpeg.wasm no worker (pode ser cancelado).
    if (audioPath.value === 'wasm') {
        const nativeBlob = await stopMediaRecorderBlob();
        isEncoding.value = true;
        encodeProgress.value = 0;
        encodeAbort = new AbortController();
        try {
            const blob = await transcodeToM4aViaWorker(nativeBlob, {
                signal: encodeAbort.signal,
                onProgress: (f) => { encodeProgress.value = Math.round(f * 100); },
                durationSec: currentDuration.value,
            });
            return { blob, duration: durationStr, waveform };
        } finally {
            isEncoding.value = false;
            encodeAbort = null;
            cleanup();
        }
    }

    // Path A / legado: MediaRecorder nativo, sem transcode.
    const finalBlob = await stopMediaRecorderBlob();
    cleanup();
    return { blob: finalBlob, duration: durationStr, waveform };
};

/**
 * Conferência final de formato para os canais Meta, feita nos BYTES.
 *
 * `MediaRecorder.isTypeSupported('audio/mp4')` não garante AAC: o Chrome em
 * máquina sem encoder AAC do sistema aceita `audio/mp4` e grava **Opus dentro
 * do MP4**. O arquivo passa em todo teste de container e de mimetype, a Meta o
 * repassa sem transcodificar, e o áudio chega MUDO no aplicativo do celular
 * (no Instagram web toca, porque quem decodifica ali é o Chrome). Era essa a
 * causa do "áudio sem som no Instagram".
 *
 * Com o mimetype pedido já explícito (`codecs=mp4a.40.2`) isso não deveria mais
 * acontecer; esta função é a rede de segurança: detectou faixa não-AAC,
 * transcoda pelo ffmpeg.wasm antes de deixar o áudio sair daqui.
 */
const garantirAac = async (blob: Blob): Promise<Blob> => {
    if (!exigeM4a.value) return blob;

    const codec = await codecDeAudioMp4(blob);
    if (codec === SAMPLE_ENTRY_AAC) return blob;

    console.warn(`[AudioRecorder] gravação saiu como "${codec ?? 'desconhecido'}" em MP4; transcodificando para AAC.`);
    isEncoding.value = true;
    encodeProgress.value = 0;
    encodeAbort = new AbortController();
    try {
        return await transcodeToM4aViaWorker(blob, {
            signal: encodeAbort.signal,
            onProgress: (f) => { encodeProgress.value = Math.round(f * 100); },
            durationSec: currentDuration.value,
        });
    } finally {
        isEncoding.value = false;
        encodeAbort = null;
    }
};

/**
 * Entrega Ogg/Opus, que é o único formato que o WhatsApp mostra como NOTA DE
 * VOZ. O Firefox já grava assim; Chrome e Edge gravam WebM com os MESMOS
 * pacotes Opus, então aqui é troca de container — nada é decodificado nem
 * recodificado.
 *
 * Falhar aqui NÃO cancela o envio: sobe o WebM como está e o servidor remuxa
 * (ele mantém a mesma rotina numa thread própria, para quem envia áudio sem
 * navegador). Perder a nota de voz por um arquivo esquisito seria pior que
 * entregar o áudio.
 */
const garantirOggOpus = async (blob: Blob): Promise<Blob> => {
    if (blob.type.includes('ogg')) return blob;

    try {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        if (!ehContainerWebm(bytes)) return blob;
        const ogg = remuxWebmOpusParaOgg(bytes);
        // `slice` do ArrayBuffer e não a vista: `BlobPart` quer um buffer
        // próprio, e assim o Blob não fica preso a um offset da vista.
        const bufferProprio = ogg.buffer.slice(ogg.byteOffset, ogg.byteOffset + ogg.byteLength) as ArrayBuffer;
        return new Blob([bufferProprio], { type: 'audio/ogg' });
    } catch (err) {
        console.warn('[AudioRecorder] remux WebM→Ogg falhou; enviando WebM e deixando o servidor remuxar.', err);
        return blob;
    }
};

/** Para a gravação e devolve um áudio que os canais Meta reproduzem de fato. */
const stopAndGetBlob = async () => {
    const resultado = await capturarBlobBruto();
    const blob = pathEhOpus(audioPath.value)
        ? await garantirOggOpus(resultado.blob)
        : await garantirAac(resultado.blob);
    return { ...resultado, blob };
};

/** Cancela uma conversão AAC/MP4 em andamento (Path B). */
const cancelEncoding = () => {
    if (encodeAbort) encodeAbort.abort();
};

const cleanup = () => {
    throttle(() => {
        stopTimer();
        clearInterval(samplingInterval);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);

        if (pcmRecorder) {
            pcmRecorder.dispose();
            pcmRecorder = null;
        }
        sourceNode = null;

        // Parar tracks do microfone
        if (streamRef) {
            streamRef.getTracks().forEach(track => track.stop());
            streamRef = null;
        }

        if (audioContext.value && audioContext.value.state !== 'closed') {
            audioContext.value.close();
        }

        if (previewBlobUrl.value) URL.revokeObjectURL(previewBlobUrl.value);

        // Reseta estados
        mode.value = 'recording'; // Reset para o estado inicial padrão
        amplitudeHistory.value = [];
        audioChunks.value = [];
    }, 250);
};

const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

onUnmounted(() => {
    cleanup();
    // Derruba o worker do ffmpeg (e o core carregado) junto com o gravador.
    disposeM4aWorker();
});

const cancelStream = () => {
    if (encodeAbort) encodeAbort.abort();
    cleanup();
};

defineExpose({ stopAndGetBlob, cancelStream });
</script>

<style scoped>
.audio-recorder-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 52px;
    padding: 0 0 0 30rem;
    border-radius: 26px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
    gap: 12px;
}
:global([data-bs-theme="dark"]) .audio-recorder-wrapper {
    background-color: #2a3942;
    color: #fff;
}

.icon-btn-delete {
    background: transparent; border: none; color: #ef476f; font-size: 20px;
    width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    transition: background 0.2s;
}
.icon-btn-delete:hover { background-color: rgba(239, 71, 111, 0.1); }

.recorder-center {
    flex-grow: 1;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
}

/* CONVERSÃO (Path B) */
.encoding-display {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 12px;
}
.btn-cancel-encode {
    background: transparent; border: none; color: #ef476f; font-size: 18px;
    width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background 0.2s;
}
.btn-cancel-encode:hover { background-color: rgba(239, 71, 111, 0.1); }

/* MODO GRAVANDO */
.recording-display {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 15px;
}
.recording-indicator { display: flex; align-items: center; gap: 8px; }
.red-dot {
    width: 10px; height: 10px; background-color: #ef476f; border-radius: 50%;
    animation: pulse 1.5s infinite;
}
.status-text { font-size: 13px; font-weight: 600; color: #ef476f; }
.timer {
    font-family: monospace; font-size: 15px; font-variant-numeric: tabular-nums;
    color: var(--bs-body-color);
    min-width: 45px;
}
:global([data-bs-theme="dark"]) .timer { color: #e9edef; }

/* Visualizer */
.visualizer-container {
    flex-grow: 1;
    height: 30px; 
    width: 100%;  
    overflow: hidden;
    display: flex; 
    align-items: center;
}
canvas { 
    width: 100%; 
    height: 100%; 
    display: block;
}

/* MODO PREVIEW */
.preview-player {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 12px;
}
.btn-preview-play {
    background: transparent; border: none; color: #8696a0; font-size: 24px;
    padding: 0; display: flex; align-items: center; cursor: pointer;
}
:global([data-bs-theme="dark"]) .btn-preview-play { color: #aebac1; }

.seek-wrapper { flex-grow: 1; display: flex; align-items: center; }
.seek-slider {
    width: 100%; -webkit-appearance: none; height: 4px; border-radius: 2px;
    background: #e9ecef; outline: none; cursor: pointer;
}
.seek-slider::-webkit-slider-thumb {
    -webkit-appearance: none; width: 12px; height: 12px; border-radius: 50%;
    background: var(--bs-success); cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}
:global([data-bs-theme="dark"]) .seek-slider { background: #374045; }

.preview-timer { font-size: 12px; color: var(--bs-text-muted); min-width: 80px; text-align: right; }

/* AÇÕES DIREITA */
.actions-right { display: flex; align-items: center; gap: 8px; }
.btn-action {
    width: 40px; height: 40px; border-radius: 50%; border: none;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; cursor: pointer; transition: transform 0.1s;
}
.btn-action:active { transform: scale(0.95); }
.btn-pause { background-color: #ef476f; color: #fff; }
.btn-resume { background-color: transparent; color: #ef476f; font-size: 22px; }

@keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.1); }
    100% { opacity: 1; transform: scale(1); }
}

@media (max-width: 991.98px) {
    .audio-recorder-wrapper {
        padding: 0 10px !important; /* Remove o padding bizarro de 30rem (480px) */
        gap: 6px;
    }
    
    /* Esconde só "Gravando..." p/ poupar espaço; mantém "Convertendo áudio…". */
    .recording-display .status-text {
        display: none;
    }
    
    .timer {
        font-size: 13px;
        min-width: auto;
    }
    
    /* Reduz levemente o botão vermelho de cancelar no mobile */
    .icon-btn-delete {
        width: 32px;
        height: 32px;
        font-size: 18px;
    }
}
</style>